import React, { useCallback } from 'react';

import { Button, Dropdown, Counter } from '@hpi-sox/hpstellar-core';
import { Cross } from '@hpi-sox/hpstellar-icons';

import { getClearAllFiltersGtmAttributes } from '../../metrics/metrics-helpers';
import { getPageUrl } from '../util';
import { ErrorBoundary } from '../../shared/components/error-boundary';

import './sort-bar.less';

export default function sortBar({
    filterCount,
    clear,
    vwaDetails,
    sortValues,
    sortGtmActions,
    onSort,
    isInfiniteScroll = false,
    loadTop,
    loadPrev,
    pageInView,
    url,
    width,
    disableSort,
    openFilters,
}) {
    const { pageSize, products, totalProducts } = vwaDetails;
    const disable = pageInView === 1;
    const productCount = Math.min(products.length * pageSize, totalProducts);
    /* set interpolated count to avoid hydration error */
    const productResults = `${productCount} of ${totalProducts}`;
    const openFilterHandler = useCallback(() => {
        setTimeout(() => {
            openFilters();
        }, 0);
    }, [openFilters]);

    const filters = (
        <div className="filterCount">
            {filterCount > 0 && (
                <>
                    <Counter className="counter" count={filterCount} size="small" subsetDelimiter="of">
                        FILTER{filterCount > 1 ? 'S' : ''} APPLIED
                    </Counter>
                    <Button
                        className="clear"
                        variation="tertiary"
                        onClick={clear}
                        endIcon={<Cross size="xs" />}
                        {...getClearAllFiltersGtmAttributes()}
                    >
                        CLEAR ALL
                    </Button>
                </>
            )}
        </div>
    );

    if (isInfiniteScroll) {
        const prevHref = getPageUrl(url, pageInView - 1, filterCount > 0, false);
        const topHref = getPageUrl(url, 1, filterCount > 0, false);

        if (width < 768) {
            return (
                <div className="vwa-mobile-results-bar">
                    <Counter className="counter" count={productResults} size="large" subsetDelimiter="of" />
                    <Button
                        className="mobileFilterButton"
                        variation="secondary"
                        color="dark"
                        size="small"
                        onClick={openFilterHandler}
                    >
                        Sort & Filter
                        {filterCount > 0 && ` (${filterCount})`}
                    </Button>
                </div>
            );
        }

        return (
            <div className="sortBar infinite">
                <div className="spacer" />
                {/* TASK: has trouble with ssr */}
                <div className="productCount infiniteCount">
                    <Counter className="counter" count={productResults} size="large" subsetDelimiter="of">
                        Results
                    </Counter>
                </div>
                <div className="controls">
                    <div className="pageLabel">{`Page ${pageInView}`}</div>
                    <div className={`buttonGroup${disable ? ' disabled' : ''}`}>
                        <a className="topButton" onClick={loadTop} href={topHref} aria-label="Go to first page">
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M16 9.72036L14.6578 11L10 6.48985L5.33747 10.9955L4 9.72036L10 4L16 9.72036ZM16 14.7204L14.6578 16L10 11.4898L5.33747 15.9955L4 14.7204L10 9L16 14.7204Z"
                                    fill="black"
                                />
                            </svg>
                        </a>
                        <a className="prevButton" onClick={loadPrev} href={prevHref} aria-label="Go to previous page">
                            <svg
                                width="12"
                                height="12"
                                viewBox="0 0 12 12"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M10.6578 9.5L12 8.22036L6 2.5L0 8.22036L1.33747 9.49549L6 4.98985L10.6578 9.5Z"
                                    fill="black"
                                />
                            </svg>
                        </a>
                    </div>
                    <ErrorBoundary>
                        <Dropdown
                            key={vwaDetails.orderBy}
                            className="sortDropdown"
                            options={sortValues}
                            onChange={onSort}
                            defaultValue={vwaDetails.orderBy}
                            gtmActions={sortGtmActions}
                            customLabel="Sort by"
                            nativeLabel="Sort by"
                        />
                    </ErrorBoundary>
                </div>
            </div>
        );
    }

    return (
        <div className="sortBar">
            {filters}

            <div className="productCount">
                <Counter className="counter" count={totalProducts} size="large" subsetDelimiter="of">
                    Result{totalProducts > 1 ? 's' : ''}
                </Counter>
            </div>
            <ErrorBoundary>
                {!disableSort && (
                    <Dropdown
                        key={vwaDetails.orderBy}
                        className="sortDropdown"
                        options={sortValues}
                        onChange={onSort}
                        defaultValue={vwaDetails.orderBy}
                        gtmActions={sortGtmActions}
                        customLabel="Sort by"
                        nativeLabel="Sort by"
                    />
                )}
            </ErrorBoundary>
        </div>
    );
}
